import type { InvitationData } from '@/src/modules/templates/types';
import {
  extractCoupleNames,
  safeFormatDateLong,
} from '@/src/infrastructure/lib/weddingDataHelper';
import type { PlotPlaneFunctionChip, PlotPlaneWeddingContent } from './plotPlaneTypes';

const DEFAULT_FUNCTIONS: PlotPlaneFunctionChip[] = [
  { key: 'mehndi', label: 'Mehndi' },
  { key: 'haldi', label: 'Haldi' },
  { key: 'sangeet', label: 'Sangeet' },
  { key: 'reception', label: 'Reception' },
];

function titleToChip(title: string): string {
  const t = title.toLowerCase();
  if (t.includes('mehndi') || t.includes('mehendi')) return 'mehndi';
  if (t.includes('haldi')) return 'haldi';
  if (t.includes('sangeet')) return 'sangeet';
  if (t.includes('reception')) return 'reception';
  if (t.includes('phera') || t.includes('wedding') || t.includes('vivah')) return 'ceremony';
  return title.slice(0, 12).toLowerCase().replace(/\s+/g, '-');
}

export function resolvePlotPlaneWeddingData(data: InvitationData): PlotPlaneWeddingContent {
  const extracted = extractCoupleNames(data.eventName);
  const groomName =
    data.customData?.wedding?.groom?.fullName ||
    data.customData?.wedding?.groom?.nickname ||
    extracted?.groom ||
    '';
  const brideName =
    data.customData?.wedding?.bride?.fullName ||
    data.customData?.wedding?.bride?.nickname ||
    extracted?.bride ||
    '';

  const coupleLine =
    groomName && brideName
      ? `${groomName} & ${brideName}`
      : groomName || brideName || data.eventName?.trim() || 'Your celebration';

  const customEvents = data.customData?.wedding?.weddingEvents;
  let functions: PlotPlaneFunctionChip[] = DEFAULT_FUNCTIONS.map((f) => ({ ...f }));

  if (customEvents && customEvents.length > 0) {
    const chips: PlotPlaneFunctionChip[] = [];
    for (const ev of customEvents) {
      const label = ev.title?.trim() || 'Event';
      const key = titleToChip(label);
      if (chips.some((c) => c.key === key)) continue;
      chips.push({
        key,
        label,
        time: ev.startTime || undefined,
        venue: ev.venue || undefined,
      });
    }
    const wanted = ['mehndi', 'haldi', 'sangeet', 'reception'];
    const ordered = wanted
      .map((k) => chips.find((c) => c.key === k))
      .filter(Boolean) as PlotPlaneFunctionChip[];
    const rest = chips.filter((c) => !wanted.includes(c.key));
    functions = [...ordered, ...rest].slice(0, 6);
    if (functions.length === 0) functions = DEFAULT_FUNCTIONS;
  }

  const dateLong = safeFormatDateLong(data.eventDate) || 'Date to be announced';
  const venue = data.eventVenue?.trim() || 'Venue to be confirmed';
  const hostName = data.hostName?.trim() || 'With love from our families';
  const customMessage =
    data.customMessage?.trim() ||
    'We invite you to walk with us through blessings, laughter, and celebration.';

  const couplePhotoUrl =
    data.mediaUrls?.[0] ||
    data.customData?.wedding?.groom?.photoUrl ||
    data.customData?.wedding?.bride?.photoUrl ||
    null;

  const invitationId = data.invitationId ?? data.id ?? null;
  const isLive = data.status === 'PUBLISHED' || data.status === 'PROCESSING';

  return {
    coupleLine,
    groomName,
    brideName,
    dateLong,
    venue,
    hostName,
    customMessage,
    functions,
    couplePhotoUrl,
    invitationId,
    isLive,
  };
}
